import React, { useState } from "react";
import { 
  Card, 
  Button, 
  Alert, 
  Table, 
  Spinner, 
  Badge,
  Form,
  Row,
  Col,
  ButtonGroup
} from "react-bootstrap";
import {
  Search,
  CheckCircle,
  XCircle,
  Book,
  Eye,
  Pencil,
  ArrowClockwise,
  Plus,
  InfoCircle
} from 'react-bootstrap-icons';
import { toast } from "react-toastify";

import {
  findSchoolByRollNumber,
  getSchoolScreeners,
  upsertSchoolScreener
} from "../services/APIClient";

import AddSchoolScreenerModal from "./AddSchoolScreenerModal";

const SchoolScreenerSearch = ({
  availableScreeningPoints = [],
  currentUser = "admin",
  getAvailableScreeningPoints // New prop to load country-specific screeners
}) => {
  // Search states
  const [rollNumber, setRollNumber] = useState("");
  const [searchedSchool, setSearchedSchool] = useState(null);
  const [schoolScreeners, setSchoolScreeners] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const [schoolSpecificScreeningPoints, setSchoolSpecificScreeningPoints] = useState([]);

  // UI states
  const [loading, setLoading] = useState(false);
  const [loadingScreeningPoints, setLoadingScreeningPoints] = useState(false);
  const [error, setError] = useState(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);

  const handleSchoolSearch = async () => {
    if (!rollNumber.trim()) {
      setError("Please enter a valid roll number.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Find the school
      const school = await findSchoolByRollNumber(rollNumber.trim());
      
      if (school && school.id) {
        setSearchedSchool(school);
        
        // Load appropriate screeners for this school's country
        await loadSchoolSpecificScreeningPoints(school);
        
        // Load screener configuration for this school
        await loadSchoolScreeners(school.id);
        
        // Add to search history (avoid duplicates)
        setSearchHistory(prev => {
          const filtered = prev.filter(s => s.id !== school.id);
          return [school, ...filtered].slice(0, 5); // Keep last 5 searches
        });
        
        toast.success("School found and screener data loaded!");
      } else {
        setError("No school found with this roll number.");
        setSearchedSchool(null);
        setSchoolScreeners([]);
        setSchoolSpecificScreeningPoints([]);
      }
    } catch (err) {
      console.error("Error fetching school:", err);
      setError("Error fetching the school. Please try again.");
      setSearchedSchool(null);
      setSchoolScreeners([]);
      setSchoolSpecificScreeningPoints([]);
    } finally {
      setLoading(false);
    }
  };

  // Load school-appropriate screening points based on school's country
  const loadSchoolSpecificScreeningPoints = async (school) => {
    if (!getAvailableScreeningPoints) {
      console.warn('getAvailableScreeningPoints function not provided, using default screening points');
      setSchoolSpecificScreeningPoints(availableScreeningPoints);
      return;
    }

    setLoadingScreeningPoints(true);
    try {
      let countryId = null;
      
      // Extract country ID from school data
      if (school.countryid) {
        countryId = school.countryid;
      } else if (school.country && school.country.id) {
        countryId = school.country.id;
      } else if (school.country) {
        countryId = school.country;
      }
      
      console.log(`Loading screening points for school: ${school.name}, country: ${countryId}`);
      
      // Load country-specific or global fallback screening points
      const screeningPoints = await getAvailableScreeningPoints(countryId);
      setSchoolSpecificScreeningPoints(screeningPoints);
      
      const countrySpecific = screeningPoints.filter(point => point.isCountrySpecific);
      if (countrySpecific.length > 0 && countryId) {
        toast.info(`Found ${countrySpecific.length} country-specific screeners available for this school!`);
      }
      
      console.log(`Loaded ${screeningPoints.length} screening points for school`);
    } catch (err) {
      console.error("Error loading school-specific screening points:", err);
      // Fallback to default screening points
      setSchoolSpecificScreeningPoints(availableScreeningPoints);
      toast.warning("Using default screeners - could not load country-specific options");
    } finally {
      setLoadingScreeningPoints(false);
    }
  };

  const loadSchoolScreeners = async (schoolId) => {
    try {
      const screeners = await getSchoolScreeners(schoolId);
      setSchoolScreeners(screeners);
    } catch (err) {
      console.error("Error loading school screeners:", err);
      setSchoolScreeners([]);
      // Don't show error for this - the school was found successfully
    }
  };

  const handleHistoryItemClick = async (school) => {
    setSearchedSchool(school);
    setRollNumber(school.rollNumber || "");
    await loadSchoolSpecificScreeningPoints(school);
    await loadSchoolScreeners(school.id);
  };

  const handleRefreshScreeners = async () => {
    if (searchedSchool) {
      await loadSchoolSpecificScreeningPoints(searchedSchool);
      await loadSchoolScreeners(searchedSchool.id);
    }
  };

  // Toggle screener active/inactive status
  const handleToggleScreener = async (screener) => {
    if (!searchedSchool) return;

    const newActiveStatus = !screener.active;
    
    try {
      setLoading(true);
      
      await upsertSchoolScreener(searchedSchool.id, screener.assessmentid, {
        active: newActiveStatus,
        dateopened: newActiveStatus ? new Date() : screener.dateopened,
        dateclosed: !newActiveStatus ? new Date() : undefined,
        created_by: screener.created_by || currentUser,
        updated_by: currentUser,
      });

      // Refresh the screener data
      await loadSchoolScreeners(searchedSchool.id);
      
      toast.success(
        `Screener ${newActiveStatus ? 'activated' : 'deactivated'} successfully!`
      );
      
    } catch (err) {
      console.error("Error toggling screener:", err);
      setError(`Failed to ${newActiveStatus ? 'activate' : 'deactivate'} screener: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // View/Edit screener details (could open a modal in the future)
  const handleViewScreener = (screener) => {
    const screenerInfo = getScreenerInfo(screener.assessmentid);
    
    // For now, show a detailed toast with screener info
    toast.info(
      <div>
        <strong>{screenerInfo?.name || 'Unknown Assessment'}</strong><br/>
        <small>
          School: {searchedSchool?.name || 'Unknown'}<br/>
          Status: {screener.active ? 'Active' : 'Inactive'}<br/>
          {screener.dateopened && `Opened: ${new Date(screener.dateopened).toLocaleDateString()}`}<br/>
          {screener.dateclosed && `Closed: ${new Date(screener.dateclosed).toLocaleDateString()}`}<br/>
          Last Updated: {screener.lastupdate ? new Date(screener.lastupdate).toLocaleDateString() : 'N/A'}
        </small>
      </div>,
      { autoClose: 5000 }
    );
  };

  // Open the modal in create mode
  const handleAddSchoolClick = () => {
    setEditingSchool(null);
    setShowAddModal(true);
  };

  // Open the modal in edit mode for the currently searched school
  const handleEditSchool = () => {
    if (!searchedSchool) return;
    setEditingSchool(searchedSchool);
    setShowAddModal(true);
  };

  const handleModalHide = () => {
    setShowAddModal(false);
    setEditingSchool(null);
  };

  const handleScreenersSaved = async (school) => {
    setShowAddModal(false);
    setEditingSchool(null);
    if (searchedSchool && searchedSchool.id === school.id) {
      await loadSchoolScreeners(school.id);
    }
  };

  const getScreenerInfo = (assessmentId) => {
    console.log(schoolSpecificScreeningPoints, "schoolSpecificScreeningPoints")
  return schoolSpecificScreeningPoints.find(point => 
    String(point.id) === String(assessmentId)
  );
};

  const getStatusBadge = (isActive) => {
    return isActive 
      ? <Badge bg="success"><CheckCircle className="me-1" />Active</Badge>
      : <Badge bg="secondary"><XCircle className="me-1" />Inactive</Badge>;
  };

  // Helper function to get country info from school
  const getSchoolCountryInfo = (school) => {
    if (!school) return null;
    
    if (school.country && typeof school.country === 'object') {
      return school.country;
    } else if (school.countryid) {
      return { id: school.countryid, name: 'Unknown Country' };
    }
    return null;
  };

  return (
    <div>
      {/* Search Section */}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <span className="fw-semibold">Search School</span>
            <Button 
              variant="success" 
              size="sm" 
              onClick={handleAddSchoolClick}
            >
              <Plus className="me-1" />
              Add New School
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="align-items-end">
            <Col md={8}>
              <Form.Group className="mb-3 mb-md-0">
                <Form.Label className="fw-semibold">School Identifier</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter roll number or URN"
                  value={rollNumber}
                  onChange={(e) => setRollNumber(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSchoolSearch()}
                  disabled={loading}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Button 
                variant="primary" 
                onClick={handleSchoolSearch}
                disabled={loading || !rollNumber.trim()}
                className="w-100"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="me-1" />
                    Search
                  </>
                )}
              </Button>
            </Col>
          </Row>

          {/* Search History */}
          {searchHistory.length > 0 && (
            <div className="mt-3">
              <small className="text-muted mb-2 d-block">Recent Searches:</small>
              <div className="d-flex flex-wrap gap-2">
                {searchHistory.map(school => (
                  <Button
                    key={school.id}
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleHistoryItemClick(school)}
                    className="text-truncate"
                    style={{ maxWidth: '200px' }}
                  >
                    {school.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* School-Specific Screening Points Info */}
          {searchedSchool && schoolSpecificScreeningPoints.length > 0 && (
            <Alert variant="info" className="mt-3 mb-0">
              <InfoCircle className="me-2" />
              <strong>Available Screeners for {searchedSchool.name}:</strong> {schoolSpecificScreeningPoints.length} screening points loaded
              {getSchoolCountryInfo(searchedSchool) && (
                <span className="ms-2">
                  <Badge bg="secondary">
                    Country: {getSchoolCountryInfo(searchedSchool).name}
                  </Badge>
                </span>
              )}
              {schoolSpecificScreeningPoints.some(point => point.isCountrySpecific) && (
                <span className="ms-2">
                  <Badge bg="info">
                    {schoolSpecificScreeningPoints.filter(point => point.isCountrySpecific).length} Country-Specific
                  </Badge>
                </span>
              )}
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          {error}
        </Alert>
      )}

      {/* School Details & Screeners */}
      {searchedSchool && (
        <Card>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <Book className="me-2 text-primary" />
                <div>
                  <span className="fw-semibold">{searchedSchool.name}</span>
                  <small className="text-muted ms-2">ID: {searchedSchool.id}</small>
                  {getSchoolCountryInfo(searchedSchool) && (
                    <small className="text-muted d-block">
                      Country: {getSchoolCountryInfo(searchedSchool).name}
                    </small>
                  )}
                </div>
              </div>
              <div>
                <Badge bg="info" className="me-2">
                  {schoolScreeners.length} Screener{schoolScreeners.length !== 1 ? 's' : ''} Configured
                </Badge>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleEditSchool}
                  className="me-2"
                  disabled={loading || loadingScreeningPoints}
                >
                  <Pencil className="me-1" />
                  Edit
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleRefreshScreeners}
                  disabled={loading || loadingScreeningPoints}
                >
                  {loadingScreeningPoints ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <ArrowClockwise className="me-1" />
                  )}
                  Refresh
                </Button>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            {(loading || loadingScreeningPoints) && (
              <div className="text-center py-4">
                <Spinner animation="border" role="status" variant="primary">
                  <span className="visually-hidden">
                    {loadingScreeningPoints ? "Loading available screeners..." : "Loading school screeners..."}
                  </span>
                </Spinner>
              </div>
            )}

            {!loading && !loadingScreeningPoints && schoolScreeners.length === 0 ? (
              <div className="text-center py-4">
                <XCircle className="text-muted mb-2" size={32} />
                <h6 className="text-muted mb-2">No Screeners Configured</h6>
                <p className="text-muted mb-3">
                  This school doesn't have any screening assessments configured yet.
                </p>
                <Alert variant="info" className="mt-3">
                  <strong>Available Screeners:</strong> {schoolSpecificScreeningPoints.length} screening points are available for configuration.
                  {schoolSpecificScreeningPoints.some(point => point.isCountrySpecific) && (
                    <>
                      <br />
                      <Badge bg="info" className="mt-1">
                        {schoolSpecificScreeningPoints.filter(point => point.isCountrySpecific).length} Country-Specific Screeners Available
                      </Badge>
                    </>
                  )}
                </Alert>
                <Button 
                  variant="primary" 
                  onClick={handleAddSchoolClick}
                >
                  <Plus className="me-1" />
                  Configure Screeners
                </Button>
              </div>
            ) : !loading && !loadingScreeningPoints && (
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th>Screening Assessment</th>
                    <th>Grade Level</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Date Opened</th>
                    <th>Date Closed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {schoolScreeners.map(screener => {
                    const screenerInfo = getScreenerInfo(screener.assessmentid);
                    
                    return (
                      <tr key={screener.id}>
                        <td>
                          <div>
                            <strong>{screenerInfo?.name || 'Unknown Assessment'}</strong>
                            {screenerInfo?.description && (
                              <>
                                <br />
                                <small className="text-muted">{screenerInfo.description}</small>
                              </>
                            )}
                            {screenerInfo?.isCountrySpecific && (
                              <>
                                <br />
                                <Badge bg="info" size="sm">Country-Specific</Badge>
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          {screenerInfo && (
                            <Badge bg={screenerInfo.isGrade2 ? "info" : "primary"}>
                              {screenerInfo.gradeLevel}
                            </Badge>
                          )}
                        </td>
                        <td>
                          {screenerInfo && (
                            <Badge bg="outline-secondary">{screenerInfo.category}</Badge>
                          )}
                        </td>
                        <td>
                          {screenerInfo && (
                            <Badge bg={screenerInfo.isCustom ? "warning" : "success"}>
                              {screenerInfo.isCustom ? "Custom" : "Standard"}
                            </Badge>
                          )}
                        </td>
                        <td>{getStatusBadge(screener.active)}</td>
                        <td>
                          {screener.dateopened ? (
                            <small className="text-muted">
                              {new Date(screener.dateopened).toLocaleDateString()}
                            </small>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {screener.dateclosed ? (
                            <small className="text-muted">
                              {new Date(screener.dateclosed).toLocaleDateString()}
                            </small>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <ButtonGroup size="sm">
                            <Button 
                              variant={screener.active ? "outline-danger" : "outline-success"}
                              onClick={() => handleToggleScreener(screener)}
                              disabled={loading}
                              title={screener.active ? "Deactivate screener" : "Activate screener"}
                            >
                              {screener.active ? <XCircle /> : <CheckCircle />}
                            </Button>
                            <Button 
                              variant="outline-info"
                              onClick={() => handleViewScreener(screener)}
                              title="View screener details"
                            >
                              <Eye />
                            </Button>
                          </ButtonGroup>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}

            {/* Summary Section */}
            {!loading && !loadingScreeningPoints && schoolScreeners.length > 0 && (
              <div className="mt-3 p-3 bg-light rounded">
                <Row>
                  <Col md={3}>
                    <div className="text-center">
                      <div className="h5 mb-0 text-success">
                        {schoolScreeners.filter(s => s.active).length}
                      </div>
                      <small className="text-muted">Active Screeners</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <div className="h5 mb-0 text-secondary">
                        {schoolScreeners.filter(s => !s.active).length}
                      </div>
                      <small className="text-muted">Inactive Screeners</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <div className="h5 mb-0 text-primary">
                        {schoolScreeners.length}
                      </div>
                      <small className="text-muted">Total Configured</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <div className="h5 mb-0 text-info">
                        {(() => {
                          const lastUpdate = Math.max(...schoolScreeners.map(s => 
                            new Date(s.lastupdate || s.dateopened || 0).getTime()
                          ));
                          return lastUpdate > 0 ? new Date(lastUpdate).toLocaleDateString() : 'N/A';
                        })()}
                      </div>
                      <small className="text-muted">Last Updated</small>
                    </div>
                  </Col>
                </Row>

                {schoolSpecificScreeningPoints.some(point => point.isCountrySpecific) && (
                  <Alert variant="info" className="mt-3 mb-0">
                    <strong>School inherits country-specific screeners:</strong> This school has access to specialized assessments designed for its country.
                    Available screeners are determined by the school's country settings.
                  </Alert>
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Add/Edit School Screener Modal */}
      <AddSchoolScreenerModal
        show={showAddModal}
        onHide={handleModalHide}
        availableScreeningPoints={
          editingSchool || searchedSchool ? schoolSpecificScreeningPoints : availableScreeningPoints
        }
        onSchoolAdded={handleScreenersSaved}
        currentUser={currentUser}
        editMode={!!editingSchool}
        initialSchool={editingSchool}
      />
    </div>
  );
};

export default SchoolScreenerSearch;